import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import { plumbingRoomNodeKeys } from '../../core/query-keys';
import { plumbingRoomNodeEndpoints } from './plumbing-room-nodes.endpoints';
import type {
  PlumbingRoomNode,
  CreatePlumbingRoomNodeRequest,
  UpdatePlumbingRoomNodeRequest,
} from './plumbing-room-nodes.types';

// Optional parent filter: pass a plumbing_room_id to scope the list to one room's nodes
// (the gateway PageQuery supports it); omit for all nodes across every plumbing room.
export function usePlumbingRoomNodes(plumbingRoomId = '') {
  return useQuery({
    queryKey: [...plumbingRoomNodeKeys.list, plumbingRoomId],
    queryFn: async () =>
      (
        await api.get<{ plumbing_room_nodes: PlumbingRoomNode[]; next_page_token: string }>(
          withQuery(plumbingRoomNodeEndpoints.list, { plumbing_room_id: plumbingRoomId || undefined }),
        )
      ).plumbing_room_nodes ?? [],
  });
}

export function usePlumbingRoomNode(id: string) {
  return useQuery({
    queryKey: plumbingRoomNodeKeys.one(id),
    queryFn: () => api.get<PlumbingRoomNode>(plumbingRoomNodeEndpoints.one(id)),
    enabled: Boolean(id),
  });
}

export function useCreatePlumbingRoomNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreatePlumbingRoomNodeRequest) =>
      api.post<PlumbingRoomNode>(plumbingRoomNodeEndpoints.list, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomNodeKeys.all }),
  });
}

export function useUpdatePlumbingRoomNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: UpdatePlumbingRoomNodeRequest & { id: string }) =>
      api.patch<PlumbingRoomNode>(plumbingRoomNodeEndpoints.one(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomNodeKeys.all }),
  });
}

export function useDeletePlumbingRoomNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(plumbingRoomNodeEndpoints.one(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: plumbingRoomNodeKeys.all }),
  });
}
